import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import React from "react";

/**
 * Shared state for the persistent application chrome — the collapsible left
 * sidebar and the always-present header bar.
 *
 * Both live above the router outlet, so pages cannot own this state locally.
 * Jotai atoms are the existing pattern for global UI state in this app (see
 * `useHelpPanel`), so the chrome follows it rather than introducing a context.
 */

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

// #region readStoredCollapsed
/**
 * Read synchronously at module load so the very first paint already has the
 * user's preference. Hydrating in an effect instead would render the wide
 * sidebar for a frame and then snap narrow, which reads as a layout glitch.
 */
const readStoredCollapsed = (): boolean => {
    try {
        return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
        return false;
    }
};

const sidebarCollapsedBaseAtom = atom<boolean>(readStoredCollapsed());

/**
 * Write-through atom: every setter call persists, so no component needs to
 * remember to sync localStorage.
 */
export const sidebarCollapsedAtom = atom<boolean, [boolean | ((prev: boolean) => boolean)], void>(
    (get) => get(sidebarCollapsedBaseAtom),
    (get, set, value) => {
        const next = typeof value === 'function' ? value(get(sidebarCollapsedBaseAtom)) : value;
        set(sidebarCollapsedBaseAtom, next);
        try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
        } catch { /* private mode — preference simply does not persist */ }
    }
);

// #region useSidebarCollapsed
export const useSidebarCollapsed = () => {
    const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
    const toggle = React.useCallback(() => setCollapsed((prev: boolean) => !prev), [setCollapsed]);
    return { collapsed, setCollapsed, toggle };
};

// #region PageBackTarget
export interface PageBackTarget {
    /** Full label, e.g. "Back to Invoices". Shown from `sm` up. */
    label: string;
    /** Short label for narrow viewports. Defaults to "Back". */
    shortLabel?: string;
    /** In-app path to navigate to. Ignored when `onBack` is supplied. */
    href?: string;
    /** Custom handler — use for `history.back()` or navigation carrying router state. */
    onBack?: () => void;
}

export const pageBackAtom = atom<PageBackTarget | null>(null);

// #region usePageBack
/**
 * Publishes a page's "Back to <x>" affordance to the global header, which owns
 * the only rendering of it. Pages no longer draw their own back button — that
 * produced eight near-identical implementations in three different styles.
 *
 * Pass `null` (or a target with no label) to publish nothing. The registration
 * is cleared on unmount so a back button never outlives its page.
 *
 * @example
 * usePageBack({ label: 'Back to Invoices', href: '/invoices' });
 * usePageBack({ label: 'Back', onBack: () => history.back() });
 */
export const usePageBack = (target: PageBackTarget | null | undefined): void => {
    const setPageBack = useSetAtom(pageBackAtom);

    const label = target?.label;
    const shortLabel = target?.shortLabel;
    const href = target?.href;
    const hasHandler = typeof target?.onBack === 'function';

    // The handler is almost always a fresh closure each render. Holding it in a
    // ref keeps the effect's dependency list stable while still calling through
    // to the latest version, so the header does not re-register every render.
    const handlerRef = React.useRef(target?.onBack);
    handlerRef.current = target?.onBack;

    React.useEffect(() => {
        if (!label) {
            setPageBack(null);
            return;
        }

        setPageBack({
            label,
            shortLabel,
            href,
            onBack: hasHandler ? () => handlerRef.current?.() : undefined
        });

        return () => setPageBack(null);
    }, [label, shortLabel, href, hasHandler, setPageBack]);
};

// #region usePageBackTarget
/** Read-only accessor for the header. */
export const usePageBackTarget = (): PageBackTarget | null => useAtomValue(pageBackAtom);

// #region PageNavTarget
/**
 * Prev/Next affordance for pages that are one item in an ordered list the user
 * already had on screen (currently order detail). Kept as its own slot rather
 * than folded into `PageBackTarget`, because the two are independent: a page
 * can have Back without siblings, and the nav callbacks churn far more often.
 */
export interface PageNavTarget {
    /** 0-based position of the current item. */
    index: number;
    /** Total items in the list the page is walking. */
    total: number;
    canPrev: boolean;
    canNext: boolean;
    /** True while the next item is being fetched — disables both controls. */
    loading?: boolean;
    onPrev: () => void;
    onNext: () => void;
}

export const pageNavAtom = atom<PageNavTarget | null>(null);

// #region usePageNav
/**
 * Publishes a page's Prev/Next controls to the global header. Pass `null` to
 * publish nothing; the registration is cleared on unmount so the controls never
 * outlive the page that owns them.
 *
 * @example
 * usePageNav(nav.available ? { index, total, canPrev, canNext, onPrev, onNext } : null);
 */
export const usePageNav = (target: PageNavTarget | null | undefined): void => {
    const setPageNav = useSetAtom(pageNavAtom);

    const index = target?.index;
    const total = target?.total;
    const canPrev = target?.canPrev;
    const canNext = target?.canNext;
    const loading = target?.loading;
    const active = !!target;

    // The handlers are rebuilt on every list mutation, so — even more than for
    // Back — they are held in a ref and kept out of the dependency list.
    const prevRef = React.useRef(target?.onPrev);
    const nextRef = React.useRef(target?.onNext);
    prevRef.current = target?.onPrev;
    nextRef.current = target?.onNext;

    React.useEffect(() => {
        if (!active) {
            setPageNav(null);
            return;
        }

        setPageNav({
            index: index ?? 0,
            total: total ?? 0,
            canPrev: !!canPrev,
            canNext: !!canNext,
            loading: !!loading,
            onPrev: () => prevRef.current?.(),
            onNext: () => nextRef.current?.()
        });

        return () => setPageNav(null);
    }, [active, index, total, canPrev, canNext, loading, setPageNav]);
};

// #region usePageNavTarget
/** Read-only accessor for the header. */
export const usePageNavTarget = (): PageNavTarget | null => useAtomValue(pageNavAtom);
